import { useEffect, useState } from 'react'
import { Badge, BlockStack, Button, Card, Collapsible, Divider, InlineStack, Spinner, Text } from '@shopify/polaris'
import { healthApi, type SettingsAuditResult, type SettingsIssue, type WorkflowReadiness } from '../../../../sdk/health.api'
import { getShop } from '../../../../sdk/shop'

type WorkflowId = 'confirmation' | 'sales' | 'shipping_confirmation' | 'customer_service'

const WF_CONFIG: Record<WorkflowId, { label: string; color: string }> = {
  confirmation:          { label: 'Confirmation',       color: '#0091ae' },
  sales:                 { label: 'Sales',               color: '#9c6ade' },
  shipping_confirmation: { label: 'Shipping Conf.',      color: '#108043' },
  customer_service:      { label: 'Customer Service',    color: '#e05c00' },
}

const SEVERITY_TONE: Record<string, React.ComponentProps<typeof Badge>['tone']> = {
  critical: 'critical',
  warning:  'warning',
  info:     'info',
}

function WorkflowRow({
  wfId,
  settingsIssues,
  readiness,
  onViewBlocker,
}: {
  wfId:           WorkflowId
  settingsIssues: SettingsIssue[]
  readiness:      WorkflowReadiness | undefined
  onViewBlocker:  (validatorId: string) => void
}) {
  const [open, setOpen] = useState(false)
  const cfg = WF_CONFIG[wfId]

  const isDisabled   = settingsIssues.some(i => i.workflow === wfId && i.code === 'disabled')
  const configIssues = settingsIssues.filter(i => i.workflow === wfId && i.code !== 'disabled')
  const blockers     = readiness?.blockers ?? []
  const dataReady    = readiness?.ready ?? true

  const hasErrors  = configIssues.length > 0 || blockers.length > 0
  const hasDetails = hasErrors

  const dotColor = isDisabled ? '#aaa' : hasErrors ? '#d82c0d' : '#008060'

  return (
    <BlockStack gap="0">
      <div
        style={{
          padding: '10px 0',
          borderBottom: open ? 'none' : '1px solid var(--p-color-border-subdued)',
        }}
      >
        <InlineStack align="space-between" blockAlign="center">
          <InlineStack gap="200" blockAlign="center">
            <div style={{ width: 10, height: 10, borderRadius: '50%', background: dotColor, flexShrink: 0 }} />
            <Text as="span" variant="bodyMd" fontWeight="semibold">{cfg.label}</Text>
            {isDisabled
              ? <Badge tone="new">Disabled</Badge>
              : <Badge tone="success">Enabled</Badge>
            }
          </InlineStack>
          <InlineStack gap="200" blockAlign="center">
            {!isDisabled && configIssues.length > 0 && (
              <Badge tone={configIssues.some(i => i.severity === 'critical') ? 'critical' : 'warning'}>
                {`${configIssues.length} config ${configIssues.length === 1 ? 'issue' : 'issues'}`}
              </Badge>
            )}
            {!isDisabled && !dataReady && (
              <Badge tone="critical">{`${blockers.length} data ${blockers.length === 1 ? 'blocker' : 'blockers'}`}</Badge>
            )}
            {!isDisabled && dataReady && configIssues.length === 0 && (
              <Badge tone="success">All clear</Badge>
            )}
            {hasDetails && (
              <Button variant="plain" size="slim" onClick={() => setOpen(o => !o)}>
                {open ? 'Hide' : 'Details'}
              </Button>
            )}
          </InlineStack>
        </InlineStack>
      </div>

      <Collapsible id={`wf-${wfId}`} open={open}>
        <div style={{ paddingBottom: 8, paddingLeft: 22, borderBottom: '1px solid var(--p-color-border-subdued)' }}>
          <BlockStack gap="150">
            {configIssues.map((issue, i) => (
              <div key={i} style={{ paddingTop: 8 }}>
                <InlineStack gap="150" blockAlign="center">
                  <Badge tone={SEVERITY_TONE[issue.severity]}>{issue.severity}</Badge>
                  <Text as="span" variant="bodySm" fontWeight="semibold">{issue.title}</Text>
                </InlineStack>
                <Text as="p" variant="bodySm" tone="subdued">{issue.description}</Text>
              </div>
            ))}
            {blockers.map((b, i) => (
              <div key={i} style={{ paddingTop: 8 }}>
                <InlineStack gap="150" blockAlign="center">
                  <Badge tone="critical">Data blocker</Badge>
                  <Text as="span" variant="bodySm" fontWeight="semibold">{b.validatorId}</Text>
                  <Text as="span" variant="bodySm" tone="subdued">({b.affectedCount} affected)</Text>
                  <Button variant="plain" size="slim" onClick={() => onViewBlocker(b.validatorId)}>Fix</Button>
                </InlineStack>
              </div>
            ))}
          </BlockStack>
        </div>
      </Collapsible>
    </BlockStack>
  )
}

type Props = {
  workflows:    WorkflowReadiness[]
  onViewIssues: (validatorId: string) => void
  refreshKey?:  number
}

export function WorkflowStatusCard({ workflows, onViewIssues, refreshKey }: Props) {
  const shop = getShop() ?? ''
  const [result,  setResult]  = useState<SettingsAuditResult | null>(null)
  const [loading, setLoading] = useState(true)
  const [error,   setError]   = useState<string | null>(null)

  function load() {
    setLoading(true)
    setError(null)
    healthApi.getSettingsAudit(shop)
      .then(setResult)
      .catch(e => setError(e instanceof Error ? e.message : 'Failed to load'))
      .finally(() => setLoading(false))
  }

  useEffect(() => { if (shop) load() }, [shop, refreshKey])

  const settingsIssues = result?.issues ?? []
  const crossIssues    = settingsIssues.filter(i => i.workflow === 'cross')

  const totalCritical = (result?.criticalCount ?? 0) + workflows.filter(w => !w.ready).reduce((s, w) => s + w.blockers.length, 0)
  const totalWarning  = result?.warningCount ?? 0

  const statusTone: React.ComponentProps<typeof Badge>['tone'] =
    !result         ? 'new'
    : totalCritical > 0 ? 'critical'
    : totalWarning  > 0 ? 'warning'
    : 'success'

  const statusLabel =
    !result         ? 'Loading'
    : totalCritical > 0 ? `${totalCritical} critical`
    : totalWarning  > 0 ? `${totalWarning} warnings`
    : 'All workflows ready'

  return (
    <Card>
      <BlockStack gap="300">
        <InlineStack align="space-between" blockAlign="center">
          <BlockStack gap="100">
            <InlineStack gap="200" blockAlign="center">
              <Text as="h3" variant="headingSm">Workflow Status</Text>
              {result && <Badge tone={statusTone}>{statusLabel}</Badge>}
            </InlineStack>
            <Text as="p" variant="bodySm" tone="subdued">
              Configuration and data readiness for each workflow
            </Text>
          </BlockStack>
        </InlineStack>

        {loading && !result && (
          <InlineStack gap="200" blockAlign="center">
            <Spinner size="small" accessibilityLabel="Loading workflow status" />
            <Text as="p" variant="bodySm" tone="subdued">Checking workflow configuration…</Text>
          </InlineStack>
        )}

        {error && <Text as="p" variant="bodySm" tone="critical">{error}</Text>}

        {result && (
          <BlockStack gap="0">
            {(['confirmation', 'sales', 'shipping_confirmation', 'customer_service'] as WorkflowId[]).map(wfId => (
              <WorkflowRow
                key={wfId}
                wfId={wfId}
                settingsIssues={settingsIssues}
                readiness={workflows.find(w => w.workflowId === wfId)}
                onViewBlocker={onViewIssues}
              />
            ))}

            {crossIssues.length > 0 && (
              <>
                <Divider />
                <div style={{ paddingTop: 8 }}>
                  <Text as="p" variant="bodySm" fontWeight="semibold" tone="subdued">Cross-Workflow</Text>
                  <BlockStack gap="100">
                    {crossIssues.map((issue, i) => (
                      <div key={i} style={{ paddingTop: 6 }}>
                        <InlineStack gap="150" blockAlign="center">
                          <Badge tone={SEVERITY_TONE[issue.severity]}>{issue.severity}</Badge>
                          <Text as="span" variant="bodySm">{issue.title}</Text>
                        </InlineStack>
                        <Text as="p" variant="bodySm" tone="subdued">{issue.description}</Text>
                      </div>
                    ))}
                  </BlockStack>
                </div>
              </>
            )}
          </BlockStack>
        )}
      </BlockStack>
    </Card>
  )
}
